import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';

/**
 * Staff Message Feed (/app/announcements) + its sidebar unread badge.
 *
 * There is no push channel to the web app, so the feed and the badge are
 * POLLED (and refetched on window focus): a license-card upload or a waitlist
 * promotion shows up within one interval without a manual refresh (BUG 450).
 */
export const MESSAGE_FEED_POLL_MS = 30 * 1000;

const FEED_KEY = ['message-feed'];
const UNREAD_KEY = ['message-feed', 'unread-count'];

export const useMessageFeed = ({ limit = 100 } = {}) =>
  useQuery({
    queryKey: [...FEED_KEY, { limit }],
    queryFn: async () => {
      const res = await api.get(`/messages?limit=${limit}`);
      return res.data.data ?? [];
    },
    refetchInterval: MESSAGE_FEED_POLL_MS,
    refetchOnWindowFocus: true,
  });

export const useMessageUnreadCount = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: UNREAD_KEY,
    queryFn: async () => {
      const res = await api.get('/messages/unread-count');
      return res.data.data?.count ?? 0;
    },
    enabled,
    refetchInterval: MESSAGE_FEED_POLL_MS,
    refetchOnWindowFocus: true,
  });

const useFeedMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FEED_KEY }),
  });
};

export const useMarkMessageRead = () =>
  useFeedMutation(async (id) => (await api.post(`/messages/${id}/read`)).data.data);

export const useMarkAllMessagesRead = () =>
  useFeedMutation(async () => (await api.post('/messages/read-all')).data.data);
